import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { FOA } from '../../types';
import Input from './Input';
import Select from './Select';
import AsyncSelect from './AsyncSelect';
import TextArea from './TextArea';
import Button from './Button';
import { contractsService } from '../../services';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useBranches } from '../../hooks/useBranches';
import { useCodePrefixes } from '../../hooks/useCodePrefixes';
import FOAAmountsSection from './FOAAmountsSection';
import FOADocumentsSection from './FOADocumentsSection';
import FOACodePrefixField from './FOACodePrefixField';

interface FOAFormProps {
  foa?: FOA;
  onSubmit: (data: Partial<FOA>) => void;
  onCancel: () => void;
}

interface FOAAmountRow {
  amount: number;
  currencyId: string;
  rate?: number;
}

interface FOAFormData {
  foaNumber: string;
  arabicName: string;
  englishName: string;
  description: string;
  branchId: string;
  contractId: string;
  status: 'active' | 'inactive' | 'completed';
  issueDate: string;
  expiryDate: string;
  assignedTo: string;
  notes: string;
  exchangeRate?: number;
  amounts: FOAAmountRow[];
}

/** Builds the initial dynamic amount rows from the saved FOA (json first, legacy fields as fallback) */
const buildInitialAmounts = (foa?: FOA): FOAAmountRow[] => {
  if (foa?.amounts && Array.isArray(foa.amounts) && foa.amounts.length > 0) {
    return foa.amounts.map((a: any) => ({
      amount: Number(a.amount) || 0,
      currencyId: a.currencyId || '',
      rate: a.rate !== undefined && a.rate !== null ? Number(a.rate) : undefined,
    }));
  }
  if (foa) {
    const legacy: FOAAmountRow[] = [];
    for (let i = 1; i <= 5; i++) {
      const amount = (foa as any)[`amount${i}`];
      const currency = (foa as any)[`currency${i}`];
      if (amount || currency) legacy.push({ amount: Number(amount) || 0, currencyId: typeof currency === 'string' ? currency : '' });
    }
    if (legacy.length > 0) return legacy;
    const fallbackCurrency: any = foa.currency;
    return [{
      amount: Number(foa.amount) || 0,
      currencyId: fallbackCurrency && typeof fallbackCurrency === 'object'
        ? (fallbackCurrency.documentId || String(fallbackCurrency.id))
        : (fallbackCurrency || ''),
    }];
  }
  return [{ amount: 0, currencyId: '' }];
};

const FOAForm: React.FC<FOAFormProps> = ({ foa, onSubmit, onCancel }) => {
  const { data: currencies } = useCurrencies();
  const branchQueryOptions = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: branches = [] } = useBranches(branchQueryOptions);
  const codePrefixQueryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 }
  }), []);
  const { data: codePrefixes = [] } = useCodePrefixes(codePrefixQueryOptions);

  const [faxFile, setFaxFile] = useState<File | null>(null);
  const [offerFile, setOfferFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FOAFormData>({
    defaultValues: foa ? {
      foaNumber: foa.foaNumber || '',
      arabicName: foa.arabicName || '',
      englishName: foa.englishName || '',
      description: foa.description || '',
      branchId: foa.branchId || '',
      contractId: foa.contractId || '',
      status: foa.status || 'active',
      issueDate: formatDateForInput(foa.issueDate),
      expiryDate: formatDateForInput(foa.expiryDate),
      assignedTo: foa.assignedTo || '',
      notes: foa.notes || '',
      amounts: buildInitialAmounts(foa),
    } : {
      foaNumber: '', arabicName: '', englishName: '', description: '',
      branchId: '', contractId: '', status: 'active', issueDate: '', expiryDate: '',
      assignedTo: '', notes: '',
      amounts: buildInitialAmounts(),
    }
  });

  const selectedBranchId = watch('branchId');

  // When the vessel/HQ changes, clear the contract (it may not belong to the new branch)
  const previousBranchRef = useRef(selectedBranchId);

  // Re-apply the saved branch once options load (edit case): the native select
  // renders before branches arrive, so the stored value needs to be re-set.
  // Older records may hold the numeric id; normalize to the documentId.
  useEffect(() => {
    if (!foa?.branchId || branches.length === 0) return;
    const match = branches.find(
      (b: any) => b.documentId === foa.branchId || String(b.id) === String(foa.branchId)
    );
    const normalized = match ? (match.documentId || String(match.id)) : foa.branchId;
    previousBranchRef.current = normalized; // don't clear the contract for the initial value
    setValue('branchId', normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foa?.branchId, branches, setValue]);
  useEffect(() => {
    if (previousBranchRef.current !== selectedBranchId) {
      previousBranchRef.current = selectedBranchId;
      setValue('contractId', '');
    }
  }, [selectedBranchId, setValue]);

  // Server-side search + pagination for the contract dropdown, scoped to the selected branch
  const fetchContracts = useCallback(async (query: string, page: number) => {
    const filters: any = {};
    if (query) filters.title = { $containsi: query };
    if (selectedBranchId) filters.branch = { documentId: { $eq: selectedBranchId } };
    return contractsService.getAll({
      filters: Object.keys(filters).length > 0 ? filters : undefined,
      pagination: { page, pageSize: 20 },
      sort: ['createdAt:desc']
    }) as any;
  }, [selectedBranchId]);

  const handleFormSubmit = (data: FOAFormData) => {
    const findCurrency = (id: string): any =>
      (currencies as any[] | undefined)?.find((c: any) => (c.documentId || String(c.id)) === id);

    const amounts = (data.amounts || [])
      .filter((row) => Number(row.amount) > 0 || row.currencyId)
      .map((row) => {
        const code = findCurrency(row.currencyId)?.code || '';
        return {
          amount: Number(row.amount) || 0,
          currency: code,
          currencyId: row.currencyId || undefined,
          // Rate to EGP only applies to non-EGP currencies
          rate: code && code !== 'EGP' && Number(row.rate) > 0 ? Number(row.rate) : undefined,
        };
      });

    const submitData: Partial<FOA> = {
      foaNumber: data.foaNumber,
      arabicName: data.arabicName,
      englishName: data.englishName,
      description: data.description,
      branchId: data.branchId,
      contractId: data.contractId || undefined,
      status: data.status,
      issueDate: formatDateForAPI(data.issueDate),
      expiryDate: formatDateForAPI(data.expiryDate),
      assignedTo: data.assignedTo,
      notes: data.notes,
      // Legacy single amount/currency/rate mirror the first row
      amount: amounts[0]?.amount ?? 0,
      currency: (amounts[0]?.currencyId as any) || undefined,
      exchangeRate: amounts[0]?.rate ?? data.exchangeRate,
      amounts,
    };
    if (faxFile) submitData.faxImage = faxFile;
    if (offerFile) submitData.offerImage = offerFile;
    onSubmit({ ...submitData, ...(foa ? { id: foa.id, documentId: foa.documentId } : {}) });
  };

  const currencyOptions = useMemo(() => {
    if (!currencies || currencies.length === 0) return [{ value: '', label: 'Select Currency' }];
    return [
      { value: '', label: 'Select Currency' },
      ...currencies
        .filter((c: any) => c.isActive !== false)
        .map((c: any) => ({
          value: c.documentId || c.id || '',
          label: `${c.code || ''} (${c.name || c.code || 'Unknown'})`
        }))
    ];
  }, [currencies]);

  const branchOpts = useMemo(() => [
    { value: '', label: 'Select Vessel/HQ' },
    ...branches.map((b: any) => ({ value: b.documentId || b.id, label: b.name }))
  ], [branches]);

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'completed', label: 'Completed' }
  ];

  const initialFaxName = foa?.faxImage && typeof foa.faxImage === 'object' && 'name' in foa.faxImage ? foa.faxImage.name : '';
  const initialOfferName = foa?.offerImage && typeof foa.offerImage === 'object' && 'name' in foa.offerImage ? foa.offerImage.name : '';

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {foa ? 'Edit FOA (امر الاسناد)' : 'Add New FOA (امر الاسناد)'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FOACodePrefixField
            register={register}
            setValue={setValue}
            errors={errors}
            codePrefixes={codePrefixes as any[]}
            isEditing={!!foa}
            initialFoaNumber={foa?.foaNumber}
          />
          <Select
            label="Status"
            {...register('status', { required: 'Status is required' })}
            options={statusOptions}
            error={errors.status?.message}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input label="Arabic Name (الاسم العربي)" {...register('arabicName', { required: 'Arabic name is required' })} error={errors.arabicName?.message} placeholder="امر اسناد رقم..." dir="rtl" />
          <Input label="English Name" {...register('englishName', { required: 'English name is required' })} error={errors.englishName?.message} placeholder="FOA Order..." />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select label="Vessel / Headquarters" {...register('branchId', { required: 'Vessel/Headquarters is required' })} options={branchOpts} error={errors.branchId?.message} />
          <Input label="Assigned To" {...register('assignedTo')} error={errors.assignedTo?.message} placeholder="Person responsible" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Controller
            name="contractId"
            control={control}
            render={({ field }) => (
              <AsyncSelect
                key={selectedBranchId || 'all'}
                label="Contract"
                name="contractId"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={selectedBranchId ? 'Select contract (optional)...' : 'Select Vessel/HQ first...'}
                fetchOptions={fetchContracts}
                getOptionLabel={(contract) => contract.title || contract.description || `Contract #${contract.id}`}
                getOptionValue={(contract) => contract.documentId || contract.id?.toString()}
              />
            )}
          />
          <div></div>
        </div>

        <TextArea label="Description" {...register('description', { required: 'Description is required' })} error={errors.description?.message} placeholder="Enter FOA description" rows={3} />

        <FOAAmountsSection
          register={register}
          errors={errors}
          control={control}
          currencyOptions={currencyOptions}
          resolveCurrencyCode={(currencyId) => {
            const c = (currencies as any[] | undefined)?.find(
              (cur: any) => (cur.documentId || String(cur.id)) === currencyId
            );
            return c?.code || '';
          }}
          getSuggestedRate={(currencyId) => {
            const c = (currencies as any[] | undefined)?.find(
              (cur: any) => (cur.documentId || String(cur.id)) === currencyId
            );
            return c?.exchangeRateToEGP || undefined;
          }}
        />

        <FOADocumentsSection initialFaxName={initialFaxName} initialOfferName={initialOfferName} onFaxFileChange={setFaxFile} onOfferFileChange={setOfferFile} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input label="Issue Date" type="date" {...register('issueDate', { required: 'Issue date is required' })} error={errors.issueDate?.message} />
          <Input label="Expiry Date" type="date" {...register('expiryDate')} error={errors.expiryDate?.message} />
        </div>

        <TextArea label="Notes" {...register('notes')} error={errors.notes?.message} placeholder="Enter any additional notes" rows={3} />

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>{foa ? 'Update FOA' : 'Create FOA'}</Button>
        </div>
      </form>
    </div>
  );
};

export default FOAForm;
